import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

export default function ColumnFilterModal({ 
  isOpen, 
  onClose, 
  field, 
  currentFilter, 
  onApplyFilter 
}) {
  const { t } = useTranslation();
  const [filterValue, setFilterValue] = useState('');
  const [operator, setOperator] = useState('contains'); // for string fields
  const [numericOperator, setNumericOperator] = useState('equals'); // for numeric fields

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setFilterValue(currentFilter?.value || '');
      if (field?.type === 'number' || field?.type === 'integer' || field?.type === 'date' || field?.name === 'created') {
        setNumericOperator(currentFilter?.operator || 'equals');
      } else {
        setOperator(currentFilter?.operator || 'contains');
      }
    }
  }, [isOpen, currentFilter, field]);

  if (!isOpen || !field) return null;

  const handleApply = () => {
    let filterOperator = operator;
    let value = filterValue;
    
    if (isNumericField) {
      filterOperator = numericOperator;
      value = Number(filterValue);
    } else if (isDateField) {
      filterOperator = numericOperator;
      value = filterValue; // Keep as string for date inputs
    }
    
    if (filterValue === '') {
      // Remove filter if empty
      onApplyFilter(field.name, null);
    } else {
      onApplyFilter(field.name, {
        value: value,
        operator: filterOperator
      });
    }
    onClose();
  };

  const handleClear = () => {
    setFilterValue('');
    onApplyFilter(field.name, null);
    onClose();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleApply();
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  const isNumericField = field.type === 'number' || field.type === 'integer';
  const isDateField = field.type === 'date' || field.name === 'created';

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            {t('singleCustomCollection.list.filters.title', { field: field.label || field.name })}
          </h3>
          
          <div className="space-y-4">
            {/* Operator selection for string fields */}
            {!isNumericField && !isDateField && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('singleCustomCollection.list.filters.filterType')}
                </label>
                <select
                  value={operator}
                  onChange={(e) => setOperator(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="contains">{t('singleCustomCollection.list.filters.operators.contains')}</option>
                  <option value="equals">{t('singleCustomCollection.list.filters.operators.equals')}</option>
                </select>
              </div>
            )}

            {/* Operator selection for numeric fields */}
            {isNumericField && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('singleCustomCollection.list.filters.filterType')}
                </label>
                <select
                  value={numericOperator}
                  onChange={(e) => setNumericOperator(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="equals">{t('singleCustomCollection.list.filters.operators.equals')}</option>
                  <option value="greater">{t('singleCustomCollection.list.filters.operators.greater')}</option>
                  <option value="less">{t('singleCustomCollection.list.filters.operators.less')}</option>
                  <option value="greaterOrEqual">{t('singleCustomCollection.list.filters.operators.greaterOrEqual')}</option>
                  <option value="lessOrEqual">{t('singleCustomCollection.list.filters.operators.lessOrEqual')}</option>
                </select>
              </div>
            )}

            {/* Operator selection for date fields */}
            {isDateField && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('singleCustomCollection.list.filters.filterType')}
                </label>
                <select
                  value={numericOperator}
                  onChange={(e) => setNumericOperator(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="equals">{t('singleCustomCollection.list.filters.operators.dateEquals')}</option>
                  <option value="greater">{t('singleCustomCollection.list.filters.operators.dateAfter')}</option>
                  <option value="less">{t('singleCustomCollection.list.filters.operators.dateBefore')}</option>
                  <option value="greaterOrEqual">{t('singleCustomCollection.list.filters.operators.dateOnOrAfter')}</option>
                  <option value="lessOrEqual">{t('singleCustomCollection.list.filters.operators.dateOnOrBefore')}</option>
                </select>
              </div>
            )}

            {/* Value input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('singleCustomCollection.list.filters.value')}
              </label>
              <input
                type={isDateField ? 'date' : isNumericField ? 'number' : 'text'}
                value={filterValue}
                onChange={(e) => setFilterValue(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={
                  isDateField
                    ? t('singleCustomCollection.list.filters.placeholders.date')
                    : isNumericField
                    ? t('singleCustomCollection.list.filters.placeholders.number')
                    : t('singleCustomCollection.list.filters.placeholders.text')
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                autoFocus
              />
            </div>
          </div>

          <div className="flex justify-between mt-6">
            <button
              onClick={handleClear}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500"
            >
              {t('singleCustomCollection.list.filters.actions.clear')}
            </button>
            <div className="space-x-2">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500"
              >
                {t('singleCustomCollection.list.filters.actions.cancel')}
              </button>
              <button
                onClick={handleApply}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {t('singleCustomCollection.list.filters.actions.apply')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
